import PropTypes from 'prop-types';
import React from 'react';
import { TRACKING_EVENTS } from '../../analytics/trackingEvent';
import TrackingButton from '../common/TrackingButton';
import { SecondaryButton } from '../mui/Button';
import { getFirstUUID } from '../../util/uuid';

/**
 * Base component for share buttons.
 * This component provides common functionality for all share buttons.
 */
export const ShareButtonBase = ({
  onClick,
  icon,
  label,
  url,
  title,
  text,
  className,
  sharedItemName,
  shareOrigin,
  onShareIntent,
  ...rest
}) => {
  const handleClick = e => {
    if (typeof onShareIntent === 'function') {
      onShareIntent({ sharedItemName, shareOrigin, method: label });
    }
    if (onClick) {
      onClick(e);
    }
  };

  return (
    // SHARE_BUTTON_CLICKED is just an intent to share. This event means the user clicked the share button,
    // but it does not guarantee that the user actually shared the item.
    <TrackingButton
      eventName={TRACKING_EVENTS.SHARE_BUTTON_CLICKED}
      trackingData={{
        // Share action method e.g. 'Copy Link', 'WhatsApp', etc.
        method: label,
        // Shared item uuid
        item_id: getFirstUUID(url) || 'unknown_item_id',
        // Content that was shared
        // - ListingPage - link to listing page
        // - AskForReviewUrl - link to order page where customer can leave a review
        // - ProfilePage - link to user profile
        content_type: sharedItemName || 'unknown_content_type',
        // Placing of the share functionality in the UI
        // - ShareListingPage - page shown after publishing a listing
        // - ProfilePage - page with user profile
        // - ListingPage_userActions - share component visible to the customer on the listing page
        // - ListingPage_shareBox - share component on the listing page below the map component
        // - ListingPage_ActionBarMaybe - share component visible to the practitioner
        //   on the listing page, when visiting own listing
        share_origin: shareOrigin || 'unknown_origin',
        // URL of the shared item
        shared_item: url,
      }}
      component={SecondaryButton}
      startIcon={icon}
      onClick={handleClick}
      className={className}
      {...rest}
    >
      {label}
    </TrackingButton>
  );
};

ShareButtonBase.propTypes = {
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.node.isRequired,
  label: PropTypes.node.isRequired,
  url: PropTypes.string.isRequired,
  title: PropTypes.string,
  text: PropTypes.string,
  className: PropTypes.string,
  onShareIntent: PropTypes.func,
};

ShareButtonBase.defaultProps = {
  title: 'Check this out',
  text: '',
  className: '',
};
